import { BIBLIO, type SendaFight } from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { CABINET_RADIUS, cabinetLayout, LECTERN_RADIUS, lecternLayout, SOOT_RADIUS, sootLayout } from '../battle/biblio-layout.ts';
import { cabinetRules, lecternRules, sootRules } from '../battle/biblio.ts';
import { createClearingMap } from '../map.ts';
import type { BPt, BookSealRules, SootRules } from '../biblio-types.ts';
import type { MapDef } from '../types.ts';

/**
 * The burnt library's grounds (the mage's path, chapter 1; docs/sendas/mago.md): the cellars (`soot`, act 1), the three
 * lecterns (`lecterns`, act 2) and the fire room (`cabinet`, act 3, the test). Apart from campaign/sendas.ts
 * (docs/SENDAS.md, «Para no chocar»): it asks this file for the ground.
 */

export const biblioGround = (act: SendaFight): boolean => act.mode === 'soot' || act.mode === 'lecterns' || act.mode === 'cabinet';

/** Their own life and blow over the chapter's commons (the library's foes carry their own: these stay at 1). */
export const biblioOwn = (act: SendaFight): { hpBp: number; dmgBp: number } | null =>
  act.mode === 'soot' ? BIBLIO.soot : act.mode === 'lecterns' ? BIBLIO.locks : act.mode === 'cabinet' ? BIBLIO.cabinet : null;

export interface BiblioGround {
  params: BattleParams;
  soot?: SootRules;
  seals?: BookSealRules;
  start: BPt;
}

const ownParams = (base: BattleParams, map: MapDef): BattleParams => {
  const { objectiveTicks: _o, objectiveQuietRate: _q, ...rest } = base;
  void _o;
  void _q;
  return {
    ...rest,
    map,
    spawns: [],
    keepHp: 1_000_000,
    keepMaxHp: 1_000_000,
    rules: {
      ...(base.rules?.enemyHpBp ? { enemyHpBp: base.rules.enemyHpBp } : {}),
    },
  };
};

/** The ground of a library's act from the seed; lives in seconds of D (`life`) at the chapter's toughness (`tough`). */
export const ownBiblio = (act: SendaFight, base: BattleParams, seed: number, life: (d: number) => number, tough: number): BiblioGround => {
  if (act.mode === 'soot') {
    const L = sootLayout(seed);
    const map: MapDef = { ...createClearingMap(SOOT_RADIUS), rocks: L.rocks };
    return {
      params: ownParams(base, map),
      soot: sootRules(L, map.radius, life, tough),
      start: L.start,
    };
  }
  if (act.mode === 'lecterns') {
    const L = lecternLayout(seed);
    const map: MapDef = {
      ...createClearingMap(LECTERN_RADIUS),
      rocks: L.rocks,
    };
    return {
      params: ownParams(base, map),
      seals: lecternRules(L, life, tough),
      start: L.start,
    };
  }
  const L = cabinetLayout(seed);
  const map: MapDef = { ...createClearingMap(CABINET_RADIUS), rocks: L.rocks };
  return {
    params: ownParams(base, map),
    seals: cabinetRules(L, map.radius, life, tough),
    start: L.start,
  };
};
